// src/AllCalendar.jsx
import React, { useEffect, useState } from 'react';
import {
    collection,
    query,
    orderBy,
    onSnapshot,
    getDocs,
    doc,
    updateDoc,
    addDoc,
    deleteDoc,
    Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import { Link } from 'react-router-dom';

function hashCode(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return hash;
}

function intToRGB(i) {
    const c = (i & 0x00FFFFFF).toString(16).toUpperCase();
    return '00000'.substring(0, 6 - c.length) + c;
}

function AllCalendar() {
    const [events, setEvents] = useState([]);
    const [resources, setResources] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // create | edit
    const [modalData, setModalData] = useState({});
    const [clients, setClients] = useState([]);

    useEffect(() => {
        const fetchResources = async () => {
            const roleSnapshot = await getDocs(collection(db, 'roles'));
            const students = [];

            roleSnapshot.forEach((doc) => {
                const data = doc.data();
                if (data.role === 'student') {
                    students.push({
                        id: doc.id,
                        title: data.displayName || 'Unnamed',
                        order: parseInt(doc.id.replace('student', '')) || 9999,
                    });
                }
            });

            students.sort((a, b) => a.order - b.order);
            setResources(students);
        };

        const fetchClients = async () => {
            const snapshot = await getDocs(collection(db, 'clients'));
            const list = snapshot.docs.map((doc) => ({
                id: doc.id,
                name: doc.data().name || '(Anonymous)',
            }));
            setClients(list);
        };

        fetchResources();
        fetchClients();
    }, []);

    useEffect(() => {
        const sessionsQuery = query(
            collection(db, 'sessions'),
            orderBy('createdAt')
        );

        const unsubscribe = onSnapshot(sessionsQuery, (snapshot) => {
            const allEvents = [];

            snapshot.forEach((docSnap) => {
                const data = docSnap.data();

                if (!data.sessionDateTime?.toDate) {
                    console.warn('Session missing valid sessionDateTime:', docSnap.id);
                    return;
                }

                const start = data.sessionDateTime.toDate();
                const end = new Date(start);
                end.setHours(end.getHours() + 1);

                allEvents.push({
                    id: docSnap.id,
                    title: `[${data.bodyPart || 'General'}] ${data.clientName || '(Anonymous)'}`,
                    start,
                    end,
                    resourceId: data.therapistUID || '',
                    color:
                        data.source === 'external'
                            ? '#f97316'
                            : '#' + intToRGB(hashCode(data.therapistUID || 'internal')),
                    extendedProps: {
                        clientId: data.clientId || '',
                        clientName: data.clientName || '(Anonymous)',
                        therapistName: data.therapistName || '',
                        therapistUID: data.therapistUID || '',
                        note: data.note || '',
                        source: data.source || 'internal',
                        bodyPart: data.bodyPart || 'General',
                        docId: docSnap.id,
                    },
                });
            });

            setEvents(allEvents);
        });

        return () => unsubscribe();
    }, []);

    const openModal = (mode, data) => {
        setModalMode(mode);
        setModalData(data);
        setShowModal(true);
    };

    const handleEventClick = (clickInfo) => {
        const event = clickInfo.event;

        openModal('edit', {
            id: event.id,
            clientId: event.extendedProps.clientId,
            clientName: event.extendedProps.clientName,
            therapistUID: event.extendedProps.therapistUID,
            dateTime: event.start.toISOString().slice(0, 16),
            bodyPart: event.extendedProps.bodyPart,
            note: event.extendedProps.note,
        });
    };

    const handleEventDrop = (info) => {
        const newDate = info.event.start;
        const newResourceId =
            info.event.getResources()[0]?.id || info.event.extendedProps.therapistUID;

        updateFirestoreEvent(info.event.id, {
            sessionDateTime: Timestamp.fromDate(newDate),
            therapistUID: newResourceId,
        });
    };

    const handleModalSave = async () => {
        if (modalMode === 'create') {
            await createFirestoreEvent(modalData);
        } else if (modalMode === 'edit') {
            await updateFirestoreEvent(modalData.id, {
                clientId: modalData.clientId,
                clientName: modalData.clientName,
                therapistUID: modalData.therapistUID,
                bodyPart: modalData.bodyPart,
                note: modalData.note,
                sessionDateTime: Timestamp.fromDate(new Date(modalData.dateTime)),
            });
        }
        setShowModal(false);
    };

    const createFirestoreEvent = async (data) => {
        await addDoc(collection(db, 'sessions'), {
            clientId: data.clientId || '',
            clientName: data.clientName || '',
            therapistUID: data.therapistUID || '',
            therapistName:
                resources.find((r) => r.id === data.therapistUID)?.title || '',
            sessionDateTime: Timestamp.fromDate(new Date(data.dateTime)),
            createdAt: Timestamp.now(),
            bodyPart: data.bodyPart || '',
            note: data.note || '',
            source: 'internal',
        });
        console.log('Created session!');
    };

    const deleteFirestoreEvent = async (docId) => {
        const ref = doc(db, 'sessions', docId);
        await deleteDoc(ref);
        console.log('Deleted session:', docId);
    };

    const updateFirestoreEvent = async (docId, updates) => {
        const docRef = doc(db, 'sessions', docId);
        await updateDoc(docRef, updates);
        console.log('Updated session:', docId, updates);
    };

    return (
        <div className="p-6 font-sans">
            <Link to="/dashboard" className="text-blue-600 hover:underline">
                ← Back to Dashboard
            </Link>

            <div className="flex items-center justify-between my-4">
                <h2 className="text-2xl font-bold">📅 All Sessions Calendar</h2>
                <button
                    onClick={() => openModal('create', {
                        clientId: '',
                        clientName: '',
                        therapistUID: '',
                        dateTime: new Date().toISOString().slice(0, 16),
                        bodyPart: '',
                        note: '',
                    })}
                    className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                >
                    ➕ Add Session
                </button>
            </div>

            <FullCalendar
                plugins={[
                    dayGridPlugin,
                    timeGridPlugin,
                    interactionPlugin,
                    resourceTimeGridPlugin,
                ]}
                initialView="resourceTimeGridDay"
                headerToolbar={{
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth,timeGridWeek,resourceTimeGridDay',
                }}
                resources={resources}
                events={events}
                eventClick={handleEventClick}
                editable={true}
                eventDrop={handleEventDrop}
                eventResize={handleEventDrop}
                droppable={true}
                dateClick={(info) => {
                    openModal('create', {
                        clientId: '',
                        clientName: '',
                        therapistUID: '',
                        dateTime: info.dateStr,
                        bodyPart: '',
                        note: '',
                    });
                }}
                eventDidMount={(info) => {
                    info.el.addEventListener('contextmenu', (e) => {
                        e.preventDefault();
                        handleEventClick({ event: info.event });
                    });
                }}
                height="auto"
                schedulerLicenseKey="GPL-My-Project-Is-Open-Source"
            />

            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded shadow w-full max-w-md space-y-4 relative">
                        <button
                            onClick={() => setShowModal(false)}
                            className="absolute top-2 right-2 text-gray-500 hover:text-black"
                        >
                            ✖
                        </button>
                        <h3 className="text-lg font-semibold">
                            {modalMode === 'create' ? 'Add Session' : 'Edit Session'}
                        </h3>

                        <div className="space-y-2">
                            <label className="block text-sm">
                                Client
                                <select
                                    value={modalData.clientId}
                                    onChange={(e) => {
                                        const clientId = e.target.value;
                                        const client = clients.find((c) => c.id === clientId);
                                        setModalData({
                                            ...modalData,
                                            clientId,
                                            clientName: client?.name || '',
                                        });
                                    }}
                                    className="w-full border px-3 py-2 rounded mt-1"
                                >
                                    <option value="">-- Select Client --</option>
                                    {clients.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.name}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label className="block text-sm">
                                Therapist
                                <select
                                    value={modalData.therapistUID}
                                    onChange={(e) =>
                                        setModalData({ ...modalData, therapistUID: e.target.value })
                                    }
                                    className="w-full border px-3 py-2 rounded mt-1"
                                >
                                    <option value="">-- Select Therapist --</option>
                                    {resources.map((t) => (
                                        <option key={t.id} value={t.id}>
                                            {t.title}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label className="block text-sm">
                                Date & Time
                                <input
                                    type="datetime-local"
                                    value={modalData.dateTime}
                                    onChange={(e) =>
                                        setModalData({ ...modalData, dateTime: e.target.value })
                                    }
                                    className="w-full border px-3 py-2 rounded mt-1"
                                />
                            </label>

                            <label className="block text-sm">
                                Body Part
                                <input
                                    type="text"
                                    value={modalData.bodyPart}
                                    onChange={(e) =>
                                        setModalData({ ...modalData, bodyPart: e.target.value })
                                    }
                                    className="w-full border px-3 py-2 rounded mt-1"
                                />
                            </label>

                            <label className="block text-sm">
                                Note
                                <textarea
                                    value={modalData.note}
                                    onChange={(e) =>
                                        setModalData({ ...modalData, note: e.target.value })
                                    }
                                    className="w-full border px-3 py-2 rounded mt-1"
                                />
                            </label>

                            <button
                                onClick={handleModalSave}
                                className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AllCalendar;
